import { useSyncExternalStore } from 'react'

/**
 * Tracks whether any `<Loading contained />` is currently on screen.
 *
 * The app-level loaders (web `App`, MS Teams `Loader`, vendor `Portal`) raise a
 * FIXED full-viewport overlay off `loadableQueries`, which lists every
 * collection query. A page that already renders its own in-place loader for
 * that same fetch therefore got a second, viewport-centred spinner stacked on
 * top of it. Suppressing the global one is safe by construction here: it only
 * yields while another loader is visible, so no fetch is ever left with no
 * feedback at all.
 *
 * Deliberately not in the zustand store: this is render-scoped plumbing
 * between two components, not app state, and routing it through the persisted
 * store is what made the onboarding lock survive reloads in the first place.
 */

let containedCount = 0
const listeners = new Set<() => void>()

const emit = (): void => {
  listeners.forEach((listener) => listener())
}

export const retainContainedLoader = (): void => {
  containedCount += 1
  emit()
}

export const releaseContainedLoader = (): void => {
  // Clamp rather than trust symmetry: React can double-invoke effects in
  // StrictMode, and a negative count would latch the global loader off.
  containedCount = Math.max(0, containedCount - 1)
  emit()
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): boolean => containedCount > 0

// The registry only ever fills from mounted components, so a server render
// has none. Required by useSyncExternalStore to avoid a hydration mismatch.
const getServerSnapshot = (): boolean => false

export const useHasContainedLoader = (): boolean => useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

// Test-only: module state outlives a single test file's renders.
export const resetContainedLoaders = (): void => {
  containedCount = 0
  emit()
}
